// Projects tab in CRM Settings. Master/detail: project list on the left,
// selected project's per-project call numbers + Meet placeholder on the right.
// Projects themselves are owned by TG host — we only read them.

import React, { useState, useMemo } from "react";
import { Loader2, Search, FolderKanban, Phone, Plus, Trash2, Star, Users, X, Pencil, Archive, Smartphone, Workflow, Plug } from "lucide-react";
import toast from "react-hot-toast";
import { Button, Badge } from "../../atoms";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";
import {
  useProjects,
  formatProject,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
} from "../../hooks/useProjects";
import {
  useProjectCallNumbers,
  useAddProjectCallNumber,
  useUpdateProjectCallNumber,
  useDeleteProjectCallNumber,
} from "../../hooks/useProjectCallNumbers";
import IntegrationsPanel from "./IntegrationsPanel";
import WorkflowsPanel from "./workflows/WorkflowsPanel";
import {
  useProjectMembers,
  useAddProjectMember,
  useRemoveProjectMember,
} from "../../hooks/useProjectMembers";
import { useTeamMembers, formatMember, useMyCrmRole, isIdentifiableMember } from "../../hooks/useTeamMembers";
import { S5_ORG_ID, getOrgId } from "../../../../utils/organization";
import { useAuth } from "../../../../contexts/AuthContext";

function statusTone(s) {
  if (s === "active") return "primary";
  if (s === "archived" || s === "completed") return "neutral";
  return "neutral";
}

function ProjectRow({ project, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%",
        padding: "10px 12px", border: "none",
        background: active ? "var(--crm-primary-soft)" : "transparent",
        borderLeft: `3px solid ${active ? "var(--crm-primary)" : "transparent"}`,
        cursor: "pointer", textAlign: "left",
      }}
    >
      <FolderKanban size={14} style={{ color: active ? "var(--crm-primary)" : "var(--color-text-secondary)" }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {formatProject(project)}
        </div>
        {project.status && (
          <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
            {project.status}
          </div>
        )}
      </span>
    </button>
  );
}

function PlivoNumbersPanel({ projectId }) {
  const { data: numbers = [], isLoading, isError, error } = useProjectCallNumbers(projectId);
  const addMut = useAddProjectCallNumber(projectId);
  const updateMut = useUpdateProjectCallNumber(projectId);
  const deleteMut = useDeleteProjectCallNumber(projectId);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ number: "", label: "" });
  const [confirmNumber, setConfirmNumber] = useState(null);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    if (!form.number.trim()) return;
    try {
      await addMut.mutateAsync({ number: form.number.trim(), label: form.label.trim() });
      setForm({ number: "", label: "" });
      setShowForm(false);
      toast.success("Number added");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to add number");
    }
  };

  const handleSetDefault = async (numberId) => {
    try {
      await updateMut.mutateAsync({ numberId, isDefault: true });
      toast.success("Default updated");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const handleDelete = (numberId) => {
    const n = numbers.find((x) => x._id === numberId);
    setConfirmNumber(n || { _id: numberId, number: "" });
  };

  const performDelete = async () => {
    if (!confirmNumber) return;
    try {
      await deleteMut.mutateAsync(confirmNumber._id);
      setConfirmNumber(null);
      toast.success("Number removed");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  return (
    <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 16 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Phone size={14} style={{ color: "var(--crm-primary)" }} />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Call numbers</h3>
          <Badge tone="neutral">{numbers.length}</Badge>
        </div>
        {!showForm && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={12} />} onClick={() => setShowForm(true)}>
            Add number
          </Button>
        )}
      </header>

      <div style={{
        display: "flex", alignItems: "flex-start", gap: 9, padding: "9px 11px",
        marginBottom: 12, borderRadius: 8,
        background: "var(--color-bg-base)", border: "1px solid var(--color-border-subtle)",
      }}>
        <Smartphone size={13} style={{ color: "var(--crm-primary)", flexShrink: 0, marginTop: 1 }} />
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: "18px" }}>
          Calls dial from these numbers over the internet and are recorded and transcribed automatically.
          On the move? Open this same app in your phone's browser and keep it open — the Call button works there too, using your phone's mic.
        </span>
      </div>

      {showForm && (
        <form onSubmit={handleAdd} style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <input
            value={form.number}
            onChange={(e) => setForm((f) => ({ ...f, number: e.target.value }))}
            placeholder="+91 98xxxxxxxx"
            autoFocus
            style={inputStyle({ flex: "1 1 180px" })}
          />
          <input
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder="Label (optional)"
            style={inputStyle({ flex: "1 1 140px" })}
          />
          <Button type="submit" size="sm" disabled={addMut.isPending}>
            {addMut.isPending ? "Adding…" : "Save"}
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => { setShowForm(false); setForm({ number: "", label: "" }); }}>
            Cancel
          </Button>
        </form>
      )}

      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, color: "var(--color-text-secondary)", fontSize: 13 }}>
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      )}
      {isError && (
        <div style={{ padding: 10, background: "var(--crm-danger-soft)", color: "var(--crm-danger)", borderRadius: 8, fontSize: 12 }}>
          {error?.response?.data?.error || error?.message || "Failed to load"}
        </div>
      )}
      {!isLoading && !isError && numbers.length === 0 && (
        <div style={{ padding: 12, fontSize: 13, color: "var(--color-text-secondary)" }}>
          No numbers configured for this project yet.
        </div>
      )}
      {numbers.length > 0 && (
        <div style={{ border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
          {numbers.map((n, i) => (
            <div
              key={n._id}
              style={{
                display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10,
                alignItems: "center", padding: "10px 12px",
                borderBottom: i < numbers.length - 1 ? "1px solid var(--color-border-subtle)" : "none",
                background: i % 2 === 0 ? "transparent" : "var(--color-bg-base)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, fontFamily: "ui-monospace, monospace" }}>{n.number}</div>
                {n.label && <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{n.label}</div>}
              </div>
              {n.isDefault ? (
                <Badge tone="primary">Default</Badge>
              ) : (
                <button
                  onClick={() => handleSetDefault(n._id)}
                  title="Set as default"
                  style={iconBtnStyle}
                >
                  <Star size={13} />
                </button>
              )}
              <button
                onClick={() => handleDelete(n._id)}
                title="Remove"
                style={{ ...iconBtnStyle, color: "var(--crm-danger)" }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
      <ConfirmDeleteModal
        open={!!confirmNumber}
        onClose={() => setConfirmNumber(null)}
        onConfirm={performDelete}
        title="Remove number from project?"
        message={confirmNumber?.number ? `${confirmNumber.number} will be removed from this project. Past call logs that used it stay intact.` : "This number will be removed from the project."}
        confirmLabel="Remove number"
        isPending={deleteMut.isPending}
      />
    </section>
  );
}



function MembersPanel({ projectId }) {
  const { data: assigned = [], isLoading, isError, error } = useProjectMembers(projectId);
  const { members: orgMembers, byId } = useTeamMembers();
  const addMut = useAddProjectMember(projectId);
  const removeMut = useRemoveProjectMember(projectId);
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState("");

  const assignedIds = useMemo(() => new Set(assigned.map((a) => String(a.userId))), [assigned]);

  const candidates = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (orgMembers || [])
      .filter(isIdentifiableMember)
      .filter((u) => !assignedIds.has(String(u._id)))
      .filter((u) => !needle || formatMember(u).toLowerCase().includes(needle) || (u.email || "").toLowerCase().includes(needle));
  }, [orgMembers, assignedIds, q]);

  const handleAdd = async (userId) => {
    try {
      await addMut.mutateAsync(userId);
      setQ("");
      toast.success("Member added");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const handleRemove = async (userId) => {
    try {
      await removeMut.mutateAsync(userId);
      toast.success("Member removed");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  return (
    <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 16 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Users size={14} style={{ color: "var(--crm-primary)" }} />
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Members</h3>
          <Badge tone="neutral">{assigned.length}</Badge>
        </div>
        {!picking && (
          <Button variant="secondary" size="sm" leftIcon={<Plus size={12} />} onClick={() => setPicking(true)}>
            Add member
          </Button>
        )}
      </header>

      {picking && (
        <div style={{ marginBottom: 12, border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 8, background: "var(--color-bg-base)" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search team members"
              style={inputStyle({ flex: 1, height: 30, fontSize: 12 })}
            />
            <Button variant="secondary" size="sm" onClick={() => { setPicking(false); setQ(""); }}>
              Done
            </Button>
          </div>
          <div style={{ maxHeight: 180, overflowY: "auto" }}>
            {candidates.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: 8 }}>
                {q ? "No matches." : "Everyone is already assigned."}
              </div>
            )}
            {candidates.map((u) => (
              <button
                key={u._id}
                type="button"
                onClick={() => handleAdd(String(u._id))}
                disabled={addMut.isPending}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "8px 10px", border: "none", background: "transparent",
                  cursor: "pointer", textAlign: "left", borderRadius: 6,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "var(--color-bg-surface)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{formatMember(u)}</div>
                  {u.email && <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{u.email}</div>}
                </span>
                <Plus size={13} style={{ color: "var(--color-text-secondary)" }} />
              </button>
            ))}
          </div>
        </div>
      )}

      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, color: "var(--color-text-secondary)", fontSize: 13 }}>
          <Loader2 size={13} className="animate-spin" /> Loading…
        </div>
      )}
      {isError && (
        <div style={{ padding: 10, background: "var(--crm-danger-soft)", color: "var(--crm-danger)", borderRadius: 8, fontSize: 12 }}>
          {error?.response?.data?.error || error?.message || "Failed to load"}
        </div>
      )}
      {!isLoading && !isError && assigned.length === 0 && (
        <div style={{ padding: 12, fontSize: 13, color: "var(--color-text-secondary)" }}>
          No members assigned yet.
        </div>
      )}
      {assigned.length > 0 && (
        <div style={{ border: "1px solid var(--color-border-subtle)", borderRadius: 8, overflow: "hidden" }}>
          {assigned.map((m, i) => {
            const u = byId.get(String(m.userId));
            return (
              <div
                key={m.id}
                style={{
                  display: "grid", gridTemplateColumns: "1fr auto", gap: 10,
                  alignItems: "center", padding: "10px 12px",
                  borderBottom: i < assigned.length - 1 ? "1px solid var(--color-border-subtle)" : "none",
                  background: i % 2 === 0 ? "transparent" : "var(--color-bg-base)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{formatMember(u) || m.userId}</div>
                  {u?.email && <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{u.email}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(m.userId)}
                  title="Remove"
                  style={{ ...iconBtnStyle, color: "var(--crm-danger)" }}
                >
                  <X size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function CreateProjectForm({ onDone }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const createMut = useCreateProject();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await createMut.mutateAsync({ name: trimmed, description: description.trim() });
      setName(""); setDescription("");
      toast.success("Project created");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to create project");
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: "flex", gap: 8, padding: 10, borderBottom: "1px solid var(--color-border-subtle)", background: "var(--color-bg-base)", flexWrap: "wrap" }}>
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Project name"
        style={inputStyle({ flex: "1 1 160px", height: 30, fontSize: 12 })}
      />
      <input
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        style={inputStyle({ flex: "1 1 160px", height: 30, fontSize: 12 })}
      />
      <Button type="submit" size="sm" disabled={createMut.isPending || !name.trim()}>
        {createMut.isPending ? "Adding…" : "Save"}
      </Button>
      <Button type="button" variant="secondary" size="sm" onClick={onDone}>Cancel</Button>
    </form>
  );
}

function EditProjectInline({ project, onDone }) {
  const [name, setName] = useState(project.name || "");
  const [description, setDescription] = useState(project.description || "");
  const updateMut = useUpdateProject();

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      await updateMut.mutateAsync({
        projectId: project._id,
        patch: { name: trimmed, description: description.trim() },
      });
      toast.success("Saved");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Project name"
        style={inputStyle({ fontSize: 14, height: 36 })}
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description"
        rows={2}
        style={{
          ...inputStyle({}),
          height: "auto", padding: 8, resize: "vertical",
        }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <Button size="sm" onClick={handleSave} disabled={updateMut.isPending || !name.trim()}>
          {updateMut.isPending ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" variant="secondary" onClick={onDone}>Cancel</Button>
      </div>
    </div>
  );
}

// Per-project sub-navigation. A pill/segmented style rather than the parent
// Settings tabs' underline, so the two nav levels stay visually distinct —
// the eye should read Projects > this project > this facet, not two rows of
// the same control.
// PROJECT_TABS_PARKED (2026-08-30, user): Workflows and Connections are hidden
// for now. Their panels stay wired below; set SHOW_PARKED_TABS = true to bring
// both tabs back. Call numbers (Plivo) are still needed for calling, so that
// panel now renders under Members instead of disappearing with Connections.
const SHOW_PARKED_TABS = false;
const DETAIL_TABS = [
  ...(SHOW_PARKED_TABS ? [
    { id: "workflows", label: "Workflows", icon: Workflow },
    { id: "connections", label: "Connections", icon: Plug },
  ] : []),
  { id: "members", label: "Members", icon: Users },
];
const DEFAULT_DETAIL_TAB = SHOW_PARKED_TABS ? "workflows" : "members";

function DetailTabs({ active, onChange }) {
  return (
    <div style={{
      display: "inline-flex", gap: 2, padding: 3, borderRadius: 9,
      background: "var(--color-bg-base)", border: "1px solid var(--color-border-subtle)",
      alignSelf: "flex-start",
    }}>
      {DETAIL_TABS.map((t) => {
        const Icon = t.icon;
        const isActive = t.id === active;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              height: 30, padding: "0 12px", borderRadius: 7, border: "none",
              cursor: "pointer", fontSize: 12.5, fontWeight: isActive ? 600 : 400,
              background: isActive ? "var(--color-bg-surface)" : "transparent",
              color: isActive ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              boxShadow: isActive ? "0 1px 2px rgba(16,24,40,0.06)" : "none",
              transition: "background-color 120ms ease, color 120ms ease",
            }}
          >
            <Icon size={13} />
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// Initial selection priority: ?projectId=... in URL (post-OAuth redirect from
// Meet / email connect) > sessionStorage pivot (set by IntegrationsPanel before
// the OAuth round-trip in case the URL param gets stripped) > null (falls
// through to auto-select first).
function initialSelectedIdFromUrl() {
  try {
    const url = new URLSearchParams(window.location.search).get("projectId");
    if (url) return String(url);
  } catch (_e) {}
  try {
    const sess = sessionStorage.getItem("crm-meet-connect-project");
    if (sess) return String(sess);
  } catch (_e) {}
  return null;
}

export default function ProjectsSection() {
  const { projects, isLoading, isError, error } = useProjects();
  const updateMut = useUpdateProject();
  const deleteMut = useDeleteProject();
  const { isAdmin } = useMyCrmRole();
  const { user } = useAuth();
  const currentOrgId = getOrgId(user);
  const showMetaConnect = currentOrgId === S5_ORG_ID;
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(() => initialSelectedIdFromUrl());
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [detailTab, setDetailTab] = useState(DEFAULT_DETAIL_TAB);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!q.trim()) return projects;
    const needle = q.toLowerCase();
    return projects.filter((p) =>
      [p.name, p.title, p.description].filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [projects, q]);

  // Once projects load: validate the deep-linked id still exists. If it does,
  // strip the URL param + sessionStorage pivot so a refresh doesn't keep
  // forcing the same project. If it doesn't (deleted, archived, lost
  // membership), fall back to auto-selecting the first available project.
  React.useEffect(() => {
    if (projects.length === 0) return;
    const exists = selectedId && projects.find((p) => String(p._id) === String(selectedId));
    if (selectedId && !exists) {
      // Deep-link target no longer in the list — clear and let auto-select run.
      setSelectedId(null);
      return;
    }
    if (!selectedId) {
      setSelectedId(String(filtered[0]?._id || projects[0]._id));
    }
    if (exists) {
      // Consumed — drop the URL param + pivot so refresh / back-nav doesn't
      // re-force this selection.
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.has("projectId")) {
          params.delete("projectId");
          const newQuery = params.toString();
          const url = window.location.pathname + (newQuery ? `?${newQuery}` : "") + window.location.hash;
          window.history.replaceState({}, "", url);
        }
      } catch (_e) {}
      try { sessionStorage.removeItem("crm-meet-connect-project"); } catch (_e) {}
    }
  }, [projects, selectedId, filtered]);

  // Reset edit mode and land back on Workflows when switching projects — a
  // sub-tab chosen for project A rarely means anything for project B.
  React.useEffect(() => { setEditing(false); setDetailTab(DEFAULT_DETAIL_TAB); }, [selectedId]);

  const selected = selectedId ? projects.find((p) => String(p._id) === selectedId) : null;

  const handleToggleArchive = async () => {
    if (!selected) return;
    const next = selected.status === "archived" ? "active" : "archived";
    try {
      await updateMut.mutateAsync({ projectId: selected._id, patch: { status: next } });
      toast.success(next === "archived" ? "Archived" : "Restored");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    setConfirmDeleteOpen(true);
  };

  const performDelete = async () => {
    if (!selected) return;
    try {
      await deleteMut.mutateAsync(selected._id);
      setConfirmDeleteOpen(false);
      setSelectedId(null);
      toast.success("Project deleted");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  return (
    <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 0, overflow: "hidden" }}>
      <header style={{ padding: 16, borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FolderKanban size={15} style={{ color: "var(--crm-primary)" }} />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Projects</h2>
            <Badge tone="neutral">{projects.length}</Badge>
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
            {isAdmin
              ? "CRM-owned projects. Leads, pipelines and onboarding all hang off these."
              : "Projects assigned to you. Contact an admin to be added to more."}
          </p>
        </div>
        {isAdmin && !creating && (
          <Button size="sm" leftIcon={<Plus size={13} />} onClick={() => setCreating(true)}>
            Add project
          </Button>
        )}
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", minHeight: 360 }}>
        <div style={{ borderRight: "1px solid var(--color-border-subtle)", display: "flex", flexDirection: "column" }}>
          {creating && <CreateProjectForm onDone={() => setCreating(false)} />}
          <div style={{ position: "relative", padding: 10, borderBottom: "1px solid var(--color-border-subtle)" }}>
            <Search size={13} style={{ position: "absolute", left: 18, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search projects"
              style={inputStyle({ width: "100%", paddingLeft: 28, height: 30, fontSize: 12 })}
            />
          </div>
          <div style={{ flex: 1, overflowY: "auto", maxHeight: 480 }}>
            {isLoading && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, color: "var(--color-text-secondary)", fontSize: 13 }}>
                <Loader2 size={13} className="animate-spin" /> Loading…
              </div>
            )}
            {isError && (
              <div style={{ padding: 12, fontSize: 12, color: "var(--crm-danger)" }}>
                {error?.response?.data?.message || error?.message || "Couldn't load projects"}
              </div>
            )}
            {!isLoading && filtered.length === 0 && (
              <div style={{ padding: 12, fontSize: 12, color: "var(--color-text-secondary)" }}>
                {q ? "No projects match." : "No projects yet."}
              </div>
            )}
            {filtered.map((p) => (
              <ProjectRow
                key={p._id}
                project={p}
                active={String(p._id) === selectedId}
                onClick={() => setSelectedId(String(p._id))}
              />
            ))}
          </div>
        </div>

        <div style={{ padding: "16px 20px", overflowY: "auto" }}>
          {!selected && (
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
              Select a project on the left to configure it.
            </div>
          )}
          {selected && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>
                  Project
                </div>
                {editing ? (
                  <div style={{ marginTop: 6 }}>
                    <EditProjectInline project={selected} onDone={() => setEditing(false)} />
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                      <h3 style={{ margin: "2px 0 0", fontSize: 18, fontWeight: 600 }}>{formatProject(selected)}</h3>
                      {isAdmin && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            type="button"
                            title="Edit project"
                            aria-label="Edit project"
                            onClick={() => setEditing(true)}
                            style={iconBtnStyle}
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            type="button"
                            title={selected.status === "archived" ? "Restore project" : "Archive project"}
                            aria-label={selected.status === "archived" ? "Restore project" : "Archive project"}
                            onClick={handleToggleArchive}
                            disabled={updateMut.isPending}
                            style={updateMut.isPending ? { ...iconBtnStyle, opacity: 0.5, cursor: "not-allowed" } : iconBtnStyle}
                          >
                            <Archive size={13} />
                          </button>
                          <button
                            type="button"
                            title="Delete project"
                            aria-label="Delete project"
                            onClick={handleDelete}
                            disabled={deleteMut.isPending}
                            style={deleteMut.isPending
                              ? { ...iconBtnStyle, color: "var(--crm-danger)", opacity: 0.5, cursor: "not-allowed" }
                              : { ...iconBtnStyle, color: "var(--crm-danger)" }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                    {selected.description && (
                      <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
                        {selected.description}
                      </p>
                    )}
                    {selected.status && (
                      <div style={{ marginTop: 6 }}>
                        <Badge tone={statusTone(selected.status)}>{selected.status}</Badge>
                      </div>
                    )}
                  </>
                )}
              </div>

              <DetailTabs active={detailTab} onChange={setDetailTab} />

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {detailTab === "workflows" && <WorkflowsPanel />}
                {detailTab === "connections" && isAdmin && (
                  <>
                    <IntegrationsPanel projectId={String(selected._id)} showMeta={showMetaConnect} />
                    <PlivoNumbersPanel projectId={String(selected._id)} />
                  </>
                )}
                {detailTab === "members" && isAdmin && (
                  <>
                    <MembersPanel projectId={String(selected._id)} />
                    {!SHOW_PARKED_TABS && <PlivoNumbersPanel projectId={String(selected._id)} />}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmDeleteModal
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={performDelete}
        title="Delete project?"
        message={selected ? `"${selected.name}" will be deleted permanently. Leads currently in this project will become orphaned (they're not deleted, but they won't show under any project until you reassign them).` : ""}
        confirmLabel="Delete project"
        isPending={deleteMut.isPending}
      />
    </section>
  );
}

function inputStyle(extra = {}) {
  return {
    height: 34, padding: "0 10px",
    borderRadius: 8, border: "1px solid var(--color-border-subtle)",
    background: "var(--color-bg-base)", color: "var(--color-text-primary)",
    fontSize: 13, outline: "none",
    ...extra,
  };
}

const iconBtnStyle = {
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 28, height: 28, border: "1px solid var(--color-border-subtle)",
  background: "var(--color-bg-base)", borderRadius: 6, cursor: "pointer",
  color: "var(--color-text-secondary)",
};
